"use client";

import { useState, useEffect, useRef } from "react";
import { usePathname } from "next/navigation";

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

const STORAGE_KEY = "pwa-install-dismissed";
const DISMISS_DAYS = 30;
const NAV_THRESHOLD = 2;

function isDismissed() {
  try {
    const val = localStorage.getItem(STORAGE_KEY);
    if (!val) return false;
    return Date.now() < Number(val);
  } catch {
    return false;
  }
}

function setDismissed() {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      String(Date.now() + DISMISS_DAYS * 24 * 60 * 60 * 1000)
    );
  } catch {
    // ignore
  }
}

function isStandalone() {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    (navigator as unknown as { standalone?: boolean }).standalone === true
  );
}

function isIosSafari() {
  const ua = navigator.userAgent;
  const isIos = /iP(hone|ad|od)/.test(ua);
  const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|OPiOS|EdgiOS/.test(ua);
  return isIos && isSafari;
}

export function InstallPrompt() {
  const pathname = usePathname();
  const navCount = useRef(0);
  const [deferredPrompt, setDeferredPrompt] =
    useState<BeforeInstallPromptEvent | null>(null);
  const [showIos, setShowIos] = useState(false);
  const [visible, setVisible] = useState(false);

  // Listen for beforeinstallprompt (Android/Chrome/Edge)
  useEffect(() => {
    if (isStandalone() || isDismissed()) return;

    function handlePrompt(e: Event) {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    }

    window.addEventListener("beforeinstallprompt", handlePrompt);
    return () =>
      window.removeEventListener("beforeinstallprompt", handlePrompt);
  }, []);

  // Detect iOS Safari
  useEffect(() => {
    if (isStandalone() || isDismissed()) return;
    // User-agent sniffing touches `navigator`, so it can only run after mount.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (isIosSafari()) setShowIos(true);
  }, []);

  // Show after N navigations
  useEffect(() => {
    navCount.current++;
    if (navCount.current >= NAV_THRESHOLD && (deferredPrompt || showIos)) {
      // Counts real navigations, which is by definition a post-render event.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setVisible(true);
    }
  }, [pathname, deferredPrompt, showIos]);

  function handleInstall() {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      deferredPrompt.userChoice.then(({ outcome }) => {
        if (outcome === "accepted") {
          setVisible(false);
        }
        setDeferredPrompt(null);
      });
    }
  }

  function handleDismiss() {
    setDismissed();
    setVisible(false);
  }

  if (!visible) return null;

  return (
    <div className="fixed bottom-4 left-4 right-4 z-50 lg:left-auto lg:right-4 lg:max-w-sm animate-fade-in">
      <div className="bg-surface rounded-2xl border border-border shadow-lg p-4">
        <div className="flex items-start gap-3">
          <img
            src="/favicon.png"
            alt=""
            className="w-10 h-10 rounded-xl shrink-0 dark:invert"
          />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-foreground">Installer Theta</p>
            {showIos && !deferredPrompt ? (
              <p className="text-xs text-muted-foreground mt-0.5">
                Tryk på{" "}
                <svg
                  className="inline -mt-0.5"
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
                  <polyline points="16 6 12 2 8 6" />
                  <line x1="12" y1="2" x2="12" y2="15" />
                </svg>{" "}
                og vælg <strong>&ldquo;Føj til hjemmeskærm&rdquo;</strong>
              </p>
            ) : (
              <p className="text-xs text-muted-foreground mt-0.5">
                Føj Theta til din hjemmeskærm for hurtigere adgang
              </p>
            )}
          </div>
          <button
            onClick={handleDismiss}
            className="p-2 -m-1 text-subtle-foreground hover:text-gray-600 transition-colors shrink-0"
            aria-label="Luk"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
        {deferredPrompt && (
          <button
            onClick={handleInstall}
            className="mt-3 w-full py-2 text-sm font-medium text-white bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 rounded-xl hover:opacity-90 transition-opacity"
          >
            Installer
          </button>
        )}
      </div>
    </div>
  );
}
